import { useRef } from "react";
import Select from "react-select";
import {
  FieldLabel,
  FormSection,
  descriptionSelectStyles,
} from "../../../../components/propertyDescription/descriptionUi";
import OwnerEmailSearchField from "../../../../components/propertyDescription/OwnerEmailSearchField";
import KeyLocationFields from "../../../../components/propertyDescription/KeyLocationFields";
import { isDigitalKeyLocation } from "../../../../utils/keyLocationRules";
import { useEditPropertyWizard } from "../EditPropertyWizardContext";
import ModalAddOwner, {
  type ModalAddOwnerHandle,
} from "../../../CreateProperty/components/DescriptionForm/ModalAddOwner";
import type { PropertyOwnerUserData } from "../../../../utils/propertyOwnerPayload";
import { PropertyAlternativeContactFields } from "../../../../components/propertyDescription/PropertyAlternativeContactFields";

export default function ResponsiblesSection() {
  const {
    formErrors,
    ownerID,
    ownerEmail,
    email,
    handleSearchChange,
    handleClearOwnerSearchResults,
    handleClearOwnerSelection,
    handleSelectUser,
    handleAddNewOwner,
    ownerUserData,
    loadingUsersRequest,
    isOwnerDropdownOpen,
    users,
    ownerDetails,
    propertyConsultant,
    consultantID,
    consultantsOptions,
    loadingUsers,
    setPropertyConsultant,
    setConsultantID,
    finderName,
    finderID,
    findersOptions,
    loadingFinders,
    setFinderName,
    setFinderID,
    keyLocationCatalog,
    keyLocationId,
    keyLocationNote,
    keyLocationPassword,
    initialKeyLocationId,
    setKeyLocationId,
    setKeyLocationNote,
    setKeyLocationPassword,
    contactEmail,
    contactPhone,
    setContactEmail,
    setContactPhone,
    setFormErrors,
  } = useEditPropertyWizard();

  const addOwnerDialog = useRef<ModalAddOwnerHandle>(null);
  const ownerEmailInputValue =
    ownerID != null || ownerUserData?.email
      ? (ownerEmail || email || ownerUserData?.email || "").trim()
      : (email || ownerEmail || "").trim();
  const hasOwner =
    ownerID != null || Boolean(ownerUserData?.email);

  const allowBlankPassword =
    isDigitalKeyLocation(keyLocationCatalog, initialKeyLocationId) &&
    isDigitalKeyLocation(keyLocationCatalog, keyLocationId);

  function handleNewOwnerFromModal(
    _identifier: string,
    userData: PropertyOwnerUserData
  ) {
    handleAddNewOwner(userData);
    addOwnerDialog.current?.close();
  }

  return (
    <FormSection
      title="Responsáveis"
      description="Busque o e-mail do proprietário ou cadastre um novo."
    >
      <ModalAddOwner
        ref={addOwnerDialog}
        onAddOwner={handleNewOwnerFromModal}
        onClose={() => addOwnerDialog.current?.close()}
      />
      <FieldLabel error={formErrors.owner_email}>Proprietário</FieldLabel>
      <OwnerEmailSearchField
        hasSelectedOwner={hasOwner}
        inputValue={ownerEmailInputValue}
        isFetching={loadingUsersRequest}
        isDropdownOpen={isOwnerDropdownOpen}
        owners={users}
        onInputChange={handleSearchChange}
        onSelectOwner={handleSelectUser}
        onClearOwner={handleClearOwnerSelection}
        onCloseDropdown={handleClearOwnerSearchResults}
        onCreateOwner={() => addOwnerDialog.current?.open()}
      />

      {hasOwner ? (
        <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
            Conta do proprietário
          </p>
          <p className="font-medium text-slate-900">
            {ownerDetails.name || "Proprietário selecionado"}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            {[ownerDetails.phone, ownerEmailInputValue]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
      ) : null}

      <PropertyAlternativeContactFields
        contactEmail={contactEmail}
        contactPhone={contactPhone}
        contactEmailError={formErrors.contact_email}
        contactPhoneError={formErrors.contact_phone}
        onContactEmailChange={(value) => {
          setContactEmail(value);
          if (formErrors.contact_email) {
            setFormErrors((current: Record<string, string>) => {
              const next = { ...current };
              delete next.contact_email;
              return next;
            });
          }
        }}
        onContactPhoneChange={(value) => {
          setContactPhone(value);
          if (formErrors.contact_phone) {
            setFormErrors((current: Record<string, string>) => {
              const next = { ...current };
              delete next.contact_phone;
              return next;
            });
          }
        }}
      />

      <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
        <div className="min-w-0">
          <FieldLabel error={formErrors.property_consultant}>
            Consultor responsável
          </FieldLabel>
          <Select
            value={{ label: propertyConsultant, value: consultantID }}
            options={consultantsOptions}
            placeholder={loadingUsers ? "Carregando" : "Selecione"}
            noOptionsMessage={() => (loadingUsers ? "Carregando" : "")}
            onChange={(e) => {
              setPropertyConsultant(e?.label);
              setConsultantID(e?.value);
            }}
            styles={descriptionSelectStyles}
          />
        </div>
        <div className="min-w-0">
          <FieldLabel>Captador</FieldLabel>
          <Select
            value={{ label: finderName, value: finderID }}
            options={findersOptions}
            placeholder={loadingFinders ? "Carregando" : "Selecione"}
            noOptionsMessage={() =>
              loadingFinders ? "Carregando" : "Nenhum captador encontrado"
            }
            onChange={(e) => {
              setFinderName(e?.label);
              setFinderID(e?.value);
            }}
            styles={descriptionSelectStyles}
          />
        </div>
      </div>
      <div className="mt-4">
        <KeyLocationFields
          catalog={keyLocationCatalog}
          keyLocationId={keyLocationId}
          keyLocationNote={keyLocationNote}
          keyLocationPassword={keyLocationPassword ?? ""}
          locationError={formErrors.key_location_note || formErrors.key_location_password}
          noteError={formErrors.key_location_note}
          passwordError={formErrors.key_location_password}
          allowBlankPassword={allowBlankPassword}
          onKeyLocationChange={(id) => {
            setKeyLocationId(id);
            if (formErrors.key_location_note || formErrors.key_location_password) {
              setFormErrors((current: Record<string, string>) => {
                const next = { ...current };
                delete next.key_location_note;
                delete next.key_location_password;
                return next;
              });
            }
          }}
          onKeyLocationNoteChange={setKeyLocationNote}
          onKeyLocationPasswordChange={setKeyLocationPassword}
        />
      </div>
    </FormSection>
  );
}
